import { expectArray, expectBoolean, expectNullable, expectRecord, expectString } from "./schema";
import { parseStudentBillingAmount, parseStudentBillingDate, parseStudentBillingIdentifier, parseStudentBillingServiceMonth } from "./student-billing";
import { STUDENT_BILLING_SAFE_PAYMENT_FAILURE_CODES } from "./student-billing-charge-execution";
export const STUDENT_BILLING_CUSTOMER_INVOICE_SAFE_FAILURE_CODES = [
    ...STUDENT_BILLING_SAFE_PAYMENT_FAILURE_CODES,
    "PROVIDER_OBJECT_MISMATCH"
];
export const STUDENT_BILLING_CUSTOMER_INVOICE_EXECUTION_STATUSES = [
    "prepared", "paymentPending", "processing", "paid", "failed", "needsAttention"
];
function exact(record, allowed, path) {
    const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
    if (unknown.length)
        throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
}
function positive(value, path) {
    if (!Number.isSafeInteger(value) || Number(value) < 1)
        throw new Error(`${path} is invalid`);
    return Number(value);
}
function iso(value, path) {
    const parsed = expectString(value, path);
    if (!Number.isFinite(new Date(parsed).getTime()))
        throw new Error(`${path} is invalid`);
    return new Date(parsed).toISOString();
}
function nullableIso(value, path) {
    return expectNullable(value, iso, path);
}
function status(value, path) {
    const parsed = expectString(value, path);
    if (!STUDENT_BILLING_CUSTOMER_INVOICE_EXECUTION_STATUSES.includes(parsed))
        throw new Error(`${path} is invalid`);
    return parsed;
}
function safeCode(value, path) {
    if (value === null)
        return null;
    const parsed = expectString(value, path);
    if (!STUDENT_BILLING_CUSTOMER_INVOICE_SAFE_FAILURE_CODES.includes(parsed))
        throw new Error(`${path} is invalid`);
    return parsed;
}
export function parseCreateStudentBillingCustomerInvoiceExecutionRequest(value, path = "customerInvoiceExecutionRequest") {
    const record = expectRecord(value, path);
    exact(record, ["expectedPreparationVersion"], path);
    return { expectedPreparationVersion: positive(record.expectedPreparationVersion, `${path}.expectedPreparationVersion`) };
}
export function parseRetryStudentBillingCustomerInvoiceExecutionRequest(value, path = "customerInvoiceExecutionRetryRequest") {
    const record = expectRecord(value, path);
    exact(record, ["expectedPreparationVersion", "expectedExecutionVersion"], path);
    return {
        expectedPreparationVersion: positive(record.expectedPreparationVersion, `${path}.expectedPreparationVersion`),
        expectedExecutionVersion: positive(record.expectedExecutionVersion, `${path}.expectedExecutionVersion`)
    };
}
export function parseStudentBillingCustomerInvoiceExecutionDto(value, path = "customerInvoiceExecution") {
    const record = expectRecord(value, path);
    exact(record, [
        "executionId", "customerInvoiceId", "payerId", "serviceMonth", "billingUnitIds", "status", "grossAmount", "currency",
        "scheduledChargeDate", "platformFeePolicyKey", "plannedApplicationFeeAmount", "attemptCount", "attemptedAt", "paidAt",
        "safeFailureCode", "canRetry", "version", "updatedAt"
    ], path);
    if (record.currency !== "JPY")
        throw new Error(`${path}.currency must be JPY`);
    const billingUnitIds = expectArray(record.billingUnitIds, parseStudentBillingIdentifier, `${path}.billingUnitIds`);
    if (!billingUnitIds.length || new Set(billingUnitIds).size !== billingUnitIds.length)
        throw new Error(`${path}.billingUnitIds is invalid`);
    const policy = expectString(record.platformFeePolicyKey, `${path}.platformFeePolicyKey`);
    if (!/^[a-f0-9]{64}$/u.test(policy))
        throw new Error(`${path}.platformFeePolicyKey is invalid`);
    return {
        executionId: parseStudentBillingIdentifier(record.executionId, `${path}.executionId`),
        customerInvoiceId: parseStudentBillingIdentifier(record.customerInvoiceId, `${path}.customerInvoiceId`),
        payerId: parseStudentBillingIdentifier(record.payerId, `${path}.payerId`),
        serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        billingUnitIds,
        status: status(record.status, `${path}.status`),
        grossAmount: parseStudentBillingAmount(record.grossAmount, `${path}.grossAmount`),
        currency: "JPY",
        scheduledChargeDate: parseStudentBillingDate(record.scheduledChargeDate, `${path}.scheduledChargeDate`),
        platformFeePolicyKey: policy,
        plannedApplicationFeeAmount: Number(record.plannedApplicationFeeAmount) === 0 ? 0 : parseStudentBillingAmount(record.plannedApplicationFeeAmount, `${path}.plannedApplicationFeeAmount`),
        attemptCount: positive(record.attemptCount, `${path}.attemptCount`),
        attemptedAt: nullableIso(record.attemptedAt, `${path}.attemptedAt`),
        paidAt: nullableIso(record.paidAt, `${path}.paidAt`),
        safeFailureCode: safeCode(record.safeFailureCode, `${path}.safeFailureCode`),
        canRetry: expectBoolean(record.canRetry, `${path}.canRetry`),
        version: positive(record.version, `${path}.version`),
        updatedAt: iso(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseStudentBillingPayerCustomerInvoiceDto(value, path = "payerCustomerInvoice") {
    const record = expectRecord(value, path);
    exact(record, ["customerInvoiceId", "serviceMonth", "billingUnitCount", "grossAmount", "currency", "scheduledChargeDate", "status", "attemptedAt", "paidAt", "safeFailureCode", "canRetry", "updatedAt"], path);
    if (record.currency !== "JPY")
        throw new Error(`${path}.currency must be JPY`);
    return {
        customerInvoiceId: parseStudentBillingIdentifier(record.customerInvoiceId, `${path}.customerInvoiceId`),
        serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        billingUnitCount: positive(record.billingUnitCount, `${path}.billingUnitCount`),
        grossAmount: parseStudentBillingAmount(record.grossAmount, `${path}.grossAmount`),
        currency: "JPY",
        scheduledChargeDate: parseStudentBillingDate(record.scheduledChargeDate, `${path}.scheduledChargeDate`),
        status: status(record.status, `${path}.status`),
        attemptedAt: nullableIso(record.attemptedAt, `${path}.attemptedAt`),
        paidAt: nullableIso(record.paidAt, `${path}.paidAt`),
        safeFailureCode: safeCode(record.safeFailureCode, `${path}.safeFailureCode`),
        canRetry: expectBoolean(record.canRetry, `${path}.canRetry`),
        updatedAt: iso(record.updatedAt, `${path}.updatedAt`)
    };
}
